import { useCallback, useState } from 'react';

import { apiService } from '../services/api';

export interface CrewRouteResult {
  route: any | null;
  jobs: any[];
  loading: boolean;
  error: string;
  refreshing: boolean;
  /** background=true skips full-page loading (e.g. after check-in refetch) */
  fetchRoute: (background?: boolean) => Promise<void>;
  refreshRoute: () => Promise<void>;
  /** Update a job with check-in completion so UI shows Job Complete/Photos immediately without waiting for refetch */
  updateJobWithCompletion: (jobId: string, completion: any) => void;
  /** Mark a job as completed so UI shows Completed immediately after complete-job success */
  updateJobCompleted: (jobId: string) => void;
}

const useCrewRoute = (): CrewRouteResult => {
  const [route, setRoute] = useState<any | null>(null);
  const [jobs, setJobs] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [refreshing, setRefreshing] = useState<boolean>(false);

  const fetchRoute = useCallback(async (background = false): Promise<void> => {
    if (!background) {
      setLoading(true);
      setError('');
    }

    try {
      // Increase timeout for crew route requests (30 seconds) - this endpoint does multiple DB queries
      const response = await apiService.get('/crew/route', {}, {
        timeout: 30000 // 30 seconds
      });

      if (response.success) {
        if (response.data) {
          setRoute(response.data);
          const nextJobs = (response.data as any).jobs || [];
          // Preserve check-in and completed state when refetch didn't return it (e.g. timezone/query edge case)
          setJobs((prev) =>
            nextJobs.map((j: any) => {
              const jId = (j.serviceRequestId || j.jobId || j._id)?.toString?.();
              const oldJob = prev.find((p: any) => (p.serviceRequestId || p.jobId || p._id)?.toString?.() === jId);
              if (!oldJob) return j;
              const preserveCheckIn = oldJob.checkInTime && !j.checkInTime;
              const preserveCompleted = oldJob.completed && !j.completed;
              const preserveOnTheWay = oldJob.onTheWaySent && !j.onTheWaySent;
              if (preserveCheckIn || preserveCompleted || preserveOnTheWay)
                return {
                  ...j,
                  ...(preserveCheckIn && { checkInTime: oldJob.checkInTime, completion: oldJob.completion }),
                  ...(preserveCompleted && { completed: true }),
                  ...(preserveOnTheWay && { onTheWaySent: true, enRouteNotifiedAt: oldJob.enRouteNotifiedAt }),
                };
              return j;
            })
          );
        } else {
          setRoute(null);
          setJobs([]);
          setError('No route assigned for today');
        }
      } else {
        setRoute(null);
        setJobs([]);
        setError(response.error || 'Failed to load route');
      }
    } catch (err: any) {
      setRoute(null);
      setJobs([]);
      setError(err?.message || 'Failed to load route');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  const refreshRoute = useCallback(async (): Promise<void> => {
    setRefreshing(true);
    await fetchRoute();
  }, [fetchRoute]);

  const updateJobWithCompletion = useCallback((jobId: string, completion: any) => {
    if (!jobId || !completion) return;
    setJobs((prev) =>
      prev.map((j) => {
        const id = (j.serviceRequestId || j.jobId || j._id)?.toString?.();
        if (id !== jobId) return j;
        return {
          ...j,
          checkInTime: completion.checkInTime ?? new Date().toISOString(),
          completion: {
            ...(j.completion || {}),
            ...completion,
            status: completion.status || 'checked_in',
          },
        };
      })
    );
  }, []);

  const updateJobCompleted = useCallback((jobId: string) => {
    if (!jobId) return;
    setJobs((prev) =>
      prev.map((j) => {
        const id = (j.serviceRequestId || j.jobId || j._id)?.toString?.();
        if (id !== jobId) return j;
        return {
          ...j,
          completed: true,
          checkInTime: j.checkInTime ?? new Date().toISOString(),
          completion: { ...(j.completion || {}), status: 'completed' },
        };
      })
    );
  }, []);

  return {
    route,
    jobs,
    loading,
    error,
    refreshing,
    fetchRoute,
    refreshRoute,
    updateJobWithCompletion,
    updateJobCompleted,
  };
};

export default useCrewRoute;
